/**
 * Section components for **Platform** entity kind in the Entity
 * Details panel. Renders these sections from Flow Q §"Frame 2"
 * + ADR-024 §4:
 *
 *   1. STATUS
 *   2. POSITION
 *   3. REGISTRATION
 *   4. CONNECTIVITY
 *   5. RECENT MESSAGES
 *   6. CONFIGURATION
 *   7. RECENT ERRORS
 *
 * The RECENT EVENTS section was removed in the ADR-032 cascade (PR6)
 * along with the rest of the data-plane Event surface; the per-producer
 * Detection rollup that supersedes it is a later increment, not a
 * drop-in replacement here.
 *
 * # Content matrix
 *
 *   | Section          | State                                                         |
 *   |------------------|---------------------------------------------------------------|
 *   | STATUS           | **Populated** from ``useProducerLatestStatus`` (issue #257)   |
 *   | POSITION         | **Populated** from ``useProducerLatestStatus`` (issue #257)   |
 *   | REGISTRATION     | **Populated** from ``useProducerDetail`` (REQ-UI-065)         |
 *   | CONNECTIVITY     | **Populated** from ``useProducerDetail`` (issue #258)         |
 *   | RECENT MESSAGES  | **Populated** from ``useProducerRates``                       |
 *   | CONFIGURATION    | **Populated** from ``useProducerDetail``                      |
 *   | RECENT ERRORS    | **Populated** from ``useRecentErrors`` (issue #259)           |
 *
 * Cross-references:
 *   - REQ-UI-049 (consolidated Entity Details panel — Platform
 *     section schema).
 *   - REQ-UI-007.
 *   - Flow Q §"Frame 2" (section content + ordering).
 *   - Flow 04 §"Frame 2" / §"Panel content per entity type"
 *     (preserved section schema from the pre-ADR-024 design).
 *   - ADR-024 §4.
 */
import { SectionShell, SectionRow, SectionPlaceholder } from "@/entity-details/section-shell";
import { useProducerDetail, type ProducerDetail } from "@/entity-details/use-producer-detail";
import {
  useProducerLatestStatus,
  type StatusItem,
  type StatusGeometry,
} from "@/entity-details/use-producer-latest-status";
import { useProducerRates } from "@/entity-details/use-producer-rates";
import { useRecentErrors } from "@/entity-details/use-recent-errors";
import { useSectionVisibility } from "@/entity-details/use-section-visibility";
import { EntityDetailsSectionsProvider } from "@/entity-details/entity-details-sections-context";
import { recordTypeForEntityKind } from "@/entity-details/entity-record-mapping";
import { SourceBytesSection } from "@/query/source-bytes-section";
import { useQueryHandoffStore } from "@/query/query-handoff-store";
import { useMultiPaneApi } from "@/multi-pane/multi-pane-context";
import { getStatusColor, healthStateToMarkerStatus } from "@/map/marker-styling";
import { usePreferences } from "@/preferences/preferences-context";
import { formatCoordinate } from "@/units/format-coordinate";
import type { components } from "@/lib/api-types.gen";

type ProducerErrorItem = components["schemas"]["ProducerErrorItem"];
type ProducerRegistrationSection = components["schemas"]["ProducerRegistrationSection"];

/**
 * Render all Platform-kind sections for the selected producer.
 *
 * Receives the producer's ID (the kind-discriminated selection's
 * id field). Each section component owns its own data fetch (or
 * not, for placeholder sections).
 *
 * Section visibility is read from
 * :func:`useSectionVisibility("platform")` per PR 5.4 / REQ-UI-092 —
 * sections the user has unchecked in the gear menu are skipped entirely
 * (header AND body, per ADR-024 §4 — "Hidden sections shall be collapsed
 * entirely (header and body both elided)"). Per-section expand/collapse
 * (REQ-UI-064) is wired by the :func:`EntityDetailsSectionsProvider`
 * below — each shell reads its own collapsed default off the context.
 */
export function PlatformSections({ producerId }: { producerId: string }): React.ReactElement {
  const detailQuery = useProducerDetail({ producerId });
  const ratesQuery = useProducerRates({ producerId });
  const recentErrorsQuery = useRecentErrors({ producerId });
  const latestStatusQuery = useProducerLatestStatus({ producerId });
  const sections = useSectionVisibility("platform");
  const { isVisible } = sections;

  // The latest-status endpoint returns the ProducerLatestStatusResponse
  // wrapper ({ status, geometry }) per PR4 4d (REQ-QUERY-026/027) — unwrap
  // the StatusItem for STATUS/POSITION and keep the geometry for the
  // STATUS field-of-view summary (REQ-UI-065). Geometry rides the wrapper,
  // never the bulk-snapshot StatusItem (REQ-QUERY-007 perf budget).
  const latestStatusResponse = latestStatusQuery.data ?? null;
  const latestStatus = latestStatusResponse?.status ?? null;
  const latestGeometry = latestStatusResponse?.geometry ?? null;
  const latestStatusError = latestStatusQuery.isError ? latestStatusQuery.error.message : null;
  const detail = detailQuery.data ?? null;

  return (
    <EntityDetailsSectionsProvider value={{ entityType: "platform", sections }}>
      {isVisible("status") && (
        <StatusSection
          status={latestStatus}
          geometry={latestGeometry}
          loading={latestStatusQuery.isLoading}
          error={latestStatusError}
        />
      )}
      {isVisible("position") && (
        <PositionSection
          status={latestStatus}
          loading={latestStatusQuery.isLoading}
          error={latestStatusError}
        />
      )}
      {isVisible("registration") && (
        <RegistrationSection
          registration={detail?.registration ?? null}
          loading={detailQuery.isLoading}
          error={detailQuery.isError ? detailQuery.error.message : null}
        />
      )}
      {isVisible("connectivity") && (
        <ConnectivitySection
          detail={detailQuery.data ?? null}
          loading={detailQuery.isLoading}
          error={detailQuery.isError ? detailQuery.error.message : null}
        />
      )}
      {isVisible("recent_messages") && (
        <RecentMessagesSection
          producerId={producerId}
          statusCount={ratesQuery.data?.status_count ?? null}
          detectionCount={ratesQuery.data?.detection_count ?? null}
          rawCount={ratesQuery.data?.raw_count ?? null}
          windowSeconds={ratesQuery.data?.window_seconds ?? 60}
          loading={ratesQuery.isLoading}
          error={ratesQuery.isError ? ratesQuery.error.message : null}
        />
      )}
      {isVisible("configuration") && (
        <ConfigurationSection
          detail={detailQuery.data ?? null}
          loading={detailQuery.isLoading}
          error={detailQuery.isError ? detailQuery.error.message : null}
        />
      )}
      {isVisible("recent_errors") && (
        <RecentErrorsSection
          items={recentErrorsQuery.data?.items ?? []}
          truncated={recentErrorsQuery.data?.truncated ?? false}
          loading={recentErrorsQuery.isLoading}
          error={recentErrorsQuery.isError ? recentErrorsQuery.error.message : null}
        />
      )}
      {/* A platform's own stored record is its registration — the queryable
          ``sapient_registration`` row keyed by producer id, whose embedded
          verbatim SAPIENT message is the registration the producer was
          admitted on (REQ-UI-184). The catalog advertised ``has_message_bytes
          = False`` for it until 2026-10-05 while the routes served the bytes
          regardless; the flag was corrected rather than the behaviour. */}
      {isVisible("source_bytes") && (
        <SourceBytesSection typeId={recordTypeForEntityKind("platform")} recordId={producerId} />
      )}
    </EntityDetailsSectionsProvider>
  );
}

// ---- STATUS + POSITION sections (populated from /latest-status) --
//
// Both consume the producer-scoped, unbounded-time latest-status
// fetch (issue #257) so they render even when the selected producer
// isn't in the current Map viewport. A null ``status`` means the
// producer has no status messages yet (the hook maps the endpoint's
// 404 to null) — an empty state, not an error.

interface LatestStatusProps {
  status: StatusItem | null;
  loading: boolean;
  error: string | null;
}

interface StatusSectionProps extends LatestStatusProps {
  geometry: StatusGeometry | null;
}

function StatusSection(props: StatusSectionProps): React.ReactElement {
  const { status, geometry, loading, error } = props;
  return (
    <SectionShell id="status" title="Status">
      {loading && <SectionPlaceholder note="Loading status…" />}
      {error !== null && <SectionPlaceholder note={`Could not load status: ${error}`} />}
      {!loading && error === null && status === null && (
        <SectionPlaceholder note="No status messages received from this platform yet." />
      )}
      {!loading && error === null && status !== null && (
        <>
          <SectionRow label="Health" value={<StatusPill healthState={status.health_state} />} />
          <SectionRow label="Last status" value={formatStatusTime(status.time)} />
          {/* Enriched SAPIENT status fields (REQ-UI-065 / REQ-QUERY-026).
           * mode_label / active_task_id / power are the new typed scalars
           * the PR4 4d slice surfaces; each is rendered only when present. */}
          {status.mode_label != null && status.mode_label !== "" && (
            <SectionRow label="Mode" value={status.mode_label} />
          )}
          {status.active_task_id != null && status.active_task_id !== "" && (
            <SectionRow
              label="Active task"
              value={<span className="font-mono">{status.active_task_id}</span>}
            />
          )}
          {status.power_level != null && (
            <SectionRow
              label="Power"
              value={formatPower(status.power_level, status.power_source, status.power_status)}
            />
          )}
          {/* Field-of-view / coverage / obscuration geometry summary
           * (REQ-UI-065 / REQ-QUERY-027). The rendered map polygons are
           * PR5b; the panel surfaces a presence summary here. */}
          <SectionRow label="Field of view" value={formatGeometrySummary(geometry)} />
          {/* CDS-arrival provenance (issue #278, REQ-UI-013). */}
          <SectionRow
            label="Network provenance"
            value={
              status.source_network
                ? `Replicated via CDS from ${status.source_network}`
                : "Locally ingested"
            }
          />
        </>
      )}
    </SectionShell>
  );
}

/**
 * Render the BSI Power triple. ``power_level`` is the primary signal;
 * ``power_source`` / ``power_status`` are SAPIENT enum ints appended raw
 * when present (the enum→label maps are a PR6 storyboard follow-up — we
 * do not invent labels here per the flag-don't-guess discipline).
 */
function formatPower(
  level: number,
  source: number | null | undefined,
  status: number | null | undefined,
): string {
  const parts = [`level ${level}`];
  if (source != null) parts.push(`source ${source}`);
  if (status != null) parts.push(`status ${status}`);
  return parts.join(" · ");
}

/**
 * Summarize the projected status geometry (REQ-UI-065). The actual map
 * polygons render in PR5b; the Entity Details panel shows presence +
 * counts so an operator knows geometry exists before toggling the layer.
 */
function formatGeometrySummary(geometry: StatusGeometry | null): string {
  if (geometry === null) return "—";
  const hasFov = Array.isArray(geometry.field_of_view) && geometry.field_of_view.length > 0;
  const coverage = geometry.coverage?.length ?? 0;
  const obscuration = geometry.obscuration?.length ?? 0;
  if (!hasFov && coverage === 0 && obscuration === 0) return "—";
  const parts: string[] = [];
  if (hasFov) parts.push("FoV defined");
  if (coverage > 0) parts.push(`${coverage} coverage`);
  if (obscuration > 0) parts.push(`${obscuration} obscuration`);
  return parts.join(" · ");
}

function PositionSection(props: LatestStatusProps): React.ReactElement {
  const { status, loading, error } = props;
  // Render the position in the operator's preferred coordinate format
  // (REQ-UI-084): one consolidated "Coordinates" row rather than separate
  // lat/lon rows, since DMS / MGRS are single tokens that span both axes.
  const format = usePreferences().workingState.units?.coordinate_format ?? "decimal_degrees";
  // Bind the position as a non-null lat/lon pair (or null when either axis is
  // absent) so the coordinate render narrows cleanly without a cast. A status
  // with null position is the location-less external-sensor case:
  // the platform reported status but no node_location, so the STATUS section
  // still populates (Health/Mode/Power) while POSITION shows the empty state
  // rather than a phantom (0, 0). Mirrors the detection/alert nullable-position
  // pattern (REQ-INGEST-044; detection-sections.tsx §GeometrySection).
  const position =
    status !== null && status.latitude_deg !== null && status.longitude_deg !== null
      ? { lat: status.latitude_deg, lon: status.longitude_deg }
      : null;
  return (
    <SectionShell id="position" title="Position">
      {loading && <SectionPlaceholder note="Loading position…" />}
      {error !== null && <SectionPlaceholder note={`Could not load position: ${error}`} />}
      {!loading && error === null && position === null && (
        // No coordinate to show — either no status at all, or a status that
        // carried no node location (the location-less external-sensor case;
        // REQ-INGEST-044). Either way POSITION is empty while STATUS renders
        // independently (operator-visibility, CLAUDE.md §8.1) — we never blank
        // the panel or show a phantom (0, 0).
        <SectionPlaceholder note="No position reported by this platform yet." />
      )}
      {!loading && error === null && position !== null && (
        <>
          <SectionRow
            label="Coordinates"
            value={
              <span className="font-mono">
                {formatCoordinate(position.lat, position.lon, format)}
              </span>
            }
          />
          {status !== null && status.elevation_m !== null && (
            <SectionRow
              label="Elevation"
              value={<span className="font-mono">{status.elevation_m.toFixed(1)} m</span>}
            />
          )}
        </>
      )}
    </SectionShell>
  );
}

/**
 * Status pill: colored dot + uppercase category label. Reuses the
 * Map's ``health_state`` → category mapping and the theme-aware
 * status color (REQ-UI-046 — status colors survive both themes) so
 * the Entity Details STATUS pill agrees with the Map markers.
 */
function StatusPill({ healthState }: { healthState: number }): React.ReactElement {
  const status = healthStateToMarkerStatus(healthState);
  return (
    <span
      className="inline-flex items-center gap-1.5 font-medium uppercase tracking-wide"
      data-testid="status-pill"
      data-status={status}
    >
      <span
        aria-hidden
        className="inline-block h-2 w-2 rounded-full"
        style={{ backgroundColor: getStatusColor(status) }}
      />
      {status.toUpperCase()}
    </span>
  );
}

function formatStatusTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// ---- REGISTRATION section (populated from /details.registration) ----
//
// The captured SAPIENT PlatformRegistration (ADR-032 §4.2; REQ-UI-065).
// ``registration`` is null when the platform was auto-registered from a
// status/detection at first sighting (the "smuggling" path) rather than
// a Registration message — the section renders that empty state.
//
// name / short_name are intentionally NOT rendered here — they ride the
// Entity Details primary header (REQ-UI-065). dependent_nodes /
// reporting_region / config_data are not surfaced by the PR4 BFF
// ProducerRegistrationSection yet (a documented follow-up).

interface RegistrationProps {
  registration: ProducerRegistrationSection | null;
  loading: boolean;
  error: string | null;
}

/** RegistrationScope enum (ADR-032 §4.2) → label. */
const REGISTRATION_SCOPE_LABELS: Record<number, string> = {
  0: "Unspecified",
  1: "Platform",
  2: "Aggregator",
};

function RegistrationSection(props: RegistrationProps): React.ReactElement {
  const { registration, loading, error } = props;
  return (
    <SectionShell id="registration" title="Registration">
      {loading && <SectionPlaceholder note="Loading registration…" />}
      {error !== null && <SectionPlaceholder note={`Could not load registration: ${error}`} />}
      {!loading && error === null && registration === null && (
        <SectionPlaceholder // Operator-facing text carries no ADR/REQ citations (architect, 2026-08-01) —
          // an operator cannot act on a document id, and it reads as leaked internals.
          note="No registration received — this platform was added automatically the first time it reported."
        />
      )}
      {!loading && error === null && registration !== null && (
        <>
          <SectionRow
            label="Scope"
            value={REGISTRATION_SCOPE_LABELS[registration.scope] ?? `Scope ${registration.scope}`}
          />
          {registration.icd_version !== null && (
            <SectionRow label="ICD version" value={registration.icd_version} />
          )}
          {registration.node_type !== null && (
            <SectionRow label="Node type" value={`Type ${registration.node_type}`} />
          )}
          <SectionRow label="Registered" value={formatStatusTime(registration.registered_at)} />
          {registration.capabilities !== undefined && registration.capabilities.length > 0 && (
            <SectionRow
              label="Capabilities"
              value={summarizeNames(
                registration.capabilities.map((c) => c.type || c.category),
                registration.capabilities.length,
                "declared",
              )}
            />
          )}
          {registration.mode_definition !== undefined &&
            registration.mode_definition.length > 0 && (
              <SectionRow
                label="Modes"
                value={summarizeNames(
                  registration.mode_definition.map((m) => m.mode_name),
                  registration.mode_definition.length,
                  "defined",
                )}
              />
            )}
          {registration.node_definition !== undefined &&
            registration.node_definition.length > 0 && (
              <SectionRow
                label="Node definitions"
                value={`${registration.node_definition.length}`}
              />
            )}
          <SectionRow
            label="Status definition"
            value={
              registration.status_definition !== null &&
              registration.status_definition !== undefined
                ? "Defined"
                : "—"
            }
          />
        </>
      )}
    </SectionShell>
  );
}

/**
 * Summarize a list of names: a comma-joined list of the non-empty names,
 * falling back to a "{count} {noun}" count when none are populated.
 */
function summarizeNames(names: readonly string[], count: number, noun: string): string {
  const joined = names.filter((s) => s !== "").join(", ");
  return joined !== "" ? joined : `${count} ${noun}`;
}

// ---- CONNECTIVITY section (populated from /details, issue #258) --

interface ConnectivityProps {
  detail: ProducerDetail | null;
  loading: boolean;
  error: string | null;
}

function ConnectivitySection(props: ConnectivityProps): React.ReactElement {
  const { detail, loading, error } = props;
  // connector_id is the discriminator: a connector_sessions row exists
  // for this producer iff the BFF populated connector_id (issue #258).
  const hasSession =
    detail !== null && detail.connector_id !== null && detail.connector_id !== undefined;
  return (
    <SectionShell id="connectivity" title="Connectivity">
      {loading && <SectionPlaceholder note="Loading connectivity…" />}
      {error !== null && <SectionPlaceholder note={`Could not load connectivity: ${error}`} />}
      {!loading && error === null && !hasSession && (
        <SectionPlaceholder note="No connector session recorded for this platform yet." />
      )}
      {!loading && error === null && hasSession && detail !== null && (
        <>
          <SectionRow
            label="Connector"
            value={<span className="font-mono">{detail.connector_id}</span>}
          />
          <SectionRow label="Session uptime" value={formatUptime(detail.session_uptime_seconds)} />
          <SectionRow
            label="Last reconnect"
            value={
              detail.last_reconnect_at !== null && detail.last_reconnect_at !== undefined
                ? formatStatusTime(detail.last_reconnect_at)
                : "None since session start"
            }
          />
        </>
      )}
    </SectionShell>
  );
}

/** Humanize a session-uptime duration (seconds → "3 h 12 min", etc.). */
function formatUptime(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  if (hours < 24) return remMin === 0 ? `${hours} h` : `${hours} h ${remMin} min`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours === 0 ? `${days} d` : `${days} d ${remHours} h`;
}

// ---- RECENT MESSAGES section (populated from /rates) -------------

interface RecentMessagesProps {
  /** The platform these counts describe — the subject of the query handoff. */
  producerId: string;
  statusCount: number | null;
  detectionCount: number | null;
  rawCount: number | null;
  windowSeconds: number;
  loading: boolean;
  error: string | null;
}

function RecentMessagesSection(props: RecentMessagesProps): React.ReactElement {
  const { producerId, statusCount, detectionCount, rawCount, windowSeconds, loading, error } =
    props;
  const window = `last ${windowSeconds} s`;
  return (
    <SectionShell id="recent_messages" title="Recent messages">
      {loading && <SectionPlaceholder note="Loading rates…" />}
      {error !== null && <SectionPlaceholder note={`Could not load rates: ${error}`} />}
      {!loading && error === null && (
        <>
          <SectionRow
            label={`Status messages (${window})`}
            value={
              <CountWithQuery
                count={statusCount === null ? "—" : statusCount}
                producerId={producerId}
                typeId="sapient_status"
                testId="platform-query-status"
                what="status messages"
              />
            }
          />
          <SectionRow
            label={`Detection messages (${window})`}
            value={
              <CountWithQuery
                count={detectionCount === null ? "—" : detectionCount}
                producerId={producerId}
                typeId="sapient_detection"
                testId="platform-query-detections"
                what="detections"
              />
            }
          />
          <SectionRow
            label={`Raw blob announcements (${window})`}
            value={
              <CountWithQuery
                count={
                  rawCount === null
                    ? "—"
                    : rawCount === 0
                      ? "0 (raw-blob source pending)"
                      : rawCount
                }
                producerId={producerId}
                typeId="raw_blob"
                testId="platform-query-raw"
                what="raw blob announcements"
              />
            }
          />
        </>
      )}
    </SectionShell>
  );
}

/**
 * A RECENT MESSAGES count with the query that shows the records behind it
 * (REQ-UI-189).
 *
 * The counts have always been a dead end: "60 status messages in the last
 * 60 s" and no way to look at them. Every SAPIENT type carries
 * ``producer_id``, so the records were queryable all along — what was missing
 * was the step from the number to the query, which the operator had to make
 * by hand by re-stating a platform already on screen.
 *
 * **Offered whether or not the count is zero.** A zero is exactly the symptom
 * an operator chases ("this platform has gone quiet"), so the way to
 * investigate must not disappear with the number (CLAUDE.md §8.1).
 *
 * Reveals the Query panel the same way the map→list sync reveals its list
 * (REQ-UI-051) — ``activatePanel`` never *opens* a closed panel, so a
 * workspace without a Query panel still gets the handoff applied and simply
 * does not jump; the request is applied whenever the panel next mounts.
 */
function CountWithQuery({
  count,
  producerId,
  typeId,
  testId,
  what,
}: {
  count: React.ReactNode;
  producerId: string;
  typeId: string;
  testId: string;
  what: string;
}): React.ReactElement {
  const requestQuery = useQueryHandoffStore((state) => state.requestQuery);
  const multiPaneApi = useMultiPaneApi();
  return (
    <span className="flex items-baseline justify-end gap-2">
      <span>{count}</span>
      <button
        type="button"
        onClick={() => {
          requestQuery({ typeId, predicates: [{ attribute: "producer_id", value: producerId }] });
          multiPaneApi?.activatePanel("query");
        }}
        data-testid={testId}
        aria-label={`Query this platform's ${what}`}
        className="rounded border border-border px-1.5 text-caption text-text-secondary hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
      >
        Query
      </button>
    </span>
  );
}

// ---- CONFIGURATION section (populated from /details) -------------

interface ConfigurationProps {
  detail: ProducerDetail | null;
  loading: boolean;
  error: string | null;
}

function ConfigurationSection(props: ConfigurationProps): React.ReactElement {
  const { detail, loading, error } = props;
  return (
    <SectionShell id="configuration" title="Configuration">
      {loading && <SectionPlaceholder note="Loading configuration…" />}
      {error !== null && <SectionPlaceholder note={`Could not load configuration: ${error}`} />}
      {!loading && error === null && detail !== null && (
        <>
          <SectionRow label="Display name" value={detail.display_name} />
          <SectionRow label="Organization" value={detail.organization} />
          <SectionRow label="Classification" value={detail.classification} />
          <SectionRow
            label="Aggregator"
            value={detail.aggregator_id ?? "Direct producer (no parent)"}
          />
          <SectionRow label="Schema version" value={detail.schema_version} />
          <SectionRow
            label="Onboarded"
            value={formatOnboarded(detail.created_at, detail.created_by)}
          />
          {detail.cert_subject_dn !== null && (
            <SectionRow label="Cert subject" value={detail.cert_subject_dn} />
          )}
          {detail.cert_fingerprint_sha256 !== null && (
            <SectionRow
              label="Cert fingerprint"
              value={shortFingerprint(detail.cert_fingerprint_sha256)}
            />
          )}
          {Object.keys(detail.metadata).length > 0 && (
            <SectionRow
              label="Metadata"
              value={
                <code className="text-body-sm text-text-primary">
                  {JSON.stringify(detail.metadata)}
                </code>
              }
            />
          )}
        </>
      )}
    </SectionShell>
  );
}

function formatOnboarded(createdAt: string, createdBy: string): string {
  // ``createdAt`` is an ISO 8601 string from the BFF. Format as
  // local-time minute precision for the Entity Details surface.
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) {
    return `${createdAt} by ${createdBy}`;
  }
  const formatted = parsed.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${formatted} by ${createdBy}`;
}

function shortFingerprint(fp: string): string {
  // ``fp`` is hex; render as the first 16 hex chars + ellipsis to
  // keep the row compact. The full value is still on the wire for
  // operators who copy from DevTools.
  return `${fp.slice(0, 16)}…`;
}

// ---- shared time formatter ---------------------------------------

/**
 * Format an ISO-8601 timestamp as local wall-clock time (HH:MM:SS).
 * Used by the RECENT ERRORS row timestamps.
 */
function formatEventTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// ---- RECENT ERRORS section (populated from /recent-errors) -------
//
// Decode / blob-write / publish failures scoped to this producer
// (issue #259). Sourced from the audit-log unsensitive view on the
// BFF — connectors have no Postgres access (ADR-002 Topology A), so
// their errors reach the query surface only via the log pipeline.

interface RecentErrorsProps {
  items: ProducerErrorItem[];
  truncated: boolean;
  loading: boolean;
  error: string | null;
}

function RecentErrorsSection(props: RecentErrorsProps): React.ReactElement {
  const { items, truncated, loading, error } = props;
  return (
    <SectionShell id="recent_errors" title="Recent errors">
      {loading && <SectionPlaceholder note="Loading errors…" />}
      {error !== null && <SectionPlaceholder note={`Could not load recent errors: ${error}`} />}
      {!loading && error === null && items.length === 0 && (
        <SectionPlaceholder note="No recent errors" />
      )}
      {!loading && error === null && items.length > 0 && (
        <ul className="flex flex-col gap-1">
          {items.map((err, i) => (
            <li
              key={`${err.timestamp}-${i}`}
              className="flex items-baseline justify-between gap-3 text-body-sm"
              data-testid="recent-error-row"
            >
              <span className="font-mono text-text-secondary">
                {formatEventTime(err.timestamp)}
              </span>
              <span className="shrink-0 font-medium uppercase text-meridian-status-unhealthy">
                {err.kind}
              </span>
              {/* `truncate` clips the summary to the panel width, so the full text
                  is carried in `title` — without it the cause is unreachable
                  (2026-09-22 live pass). */}
              <span className="flex-1 truncate text-text-primary" title={err.message}>
                {err.message}
              </span>
            </li>
          ))}
          {truncated && (
            <li className="pt-1 text-caption italic text-text-muted">
              + more errors available (showing {items.length})
            </li>
          )}
        </ul>
      )}
    </SectionShell>
  );
}
